import type { ScreenTourMap, ScreenTourStep } from './types';

/**
 * Reportes (los que no son pilotos). Los filtros comunes (fechas, sede, rutas, Consultar y Exportar CSV)
 * usan los textos compartidos `tours.reportFilters.*`; lo propio de cada reporte va en `tours.<pantalla>.*`.
 */

/** Fechas, sede, rutas y Consultar con las marcas `<prefijo>-start|end|office|routes|query`. */
const filters = (prefix: string): ScreenTourStep[] => [
  { text: 'tours.reportFilters.start', target: `${prefix}-start` },
  { text: 'tours.reportFilters.end', target: `${prefix}-end` },
  { text: 'tours.reportFilters.office', target: `${prefix}-office` },
  { text: 'tours.reportFilters.routes', target: `${prefix}-routes` },
  { text: 'tours.reportFilters.query', target: `${prefix}-query` },
];

const exportCsv = (prefix: string): ScreenTourStep => ({ text: 'tours.reportFilters.export', target: `${prefix}-export` });

export const reportMoreTours: ScreenTourMap = {
  '/reports/app-sync': {
    key: 'screen-app-sync',
    steps: [
      { text: 'tours.appSync.welcome' },
      { text: 'tours.reportFilters.start', target: 'app-sync-start' },
      { text: 'tours.reportFilters.end', target: 'app-sync-end' },
      { text: 'tours.reportFilters.office', target: 'app-sync-office' },
      { text: 'tours.reportFilters.routes', target: 'app-sync-routes' },
      { text: 'tours.appSync.all', target: 'app-sync-all' },
      { text: 'tours.reportFilters.query', target: 'app-sync-query' },
      { text: 'tours.appSync.table', target: 'app-sync' },
      { text: 'tours.appSync.percentage', target: 'app-sync-col-transmissionPercentage', whenMissing: 'center' },
      { text: 'tours.appSync.days', target: 'app-sync-table', whenMissing: 'center' },
      exportCsv('app-sync'),
    ],
  },
  '/reports/balances': {
    key: 'screen-balances',
    steps: [
      { text: 'tours.balances.welcome' },
      ...filters('balances'),
      { text: 'tours.balances.table', target: 'balances' },
      { text: 'tours.balances.portfolio', target: 'balances-col-portfolio', whenMissing: 'center' },
      { text: 'tours.balances.defaultClients', target: 'balances-col-defaultClientCount', whenMissing: 'center' },
      { text: 'tours.balances.defaultValue', target: 'balances-col-defaultValue', whenMissing: 'center' },
      { text: 'tours.balances.cashBox', target: 'balances-col-cashBox', whenMissing: 'center' },
      exportCsv('balances'),
    ],
  },
  '/reports/cancellations': {
    key: 'screen-cancellations',
    steps: [
      { text: 'tours.cancellations.welcome' },
      ...filters('cancellations'),
      { text: 'tours.cancellations.stats', target: 'cancellations-stats', whenMissing: 'center' },
      { text: 'tours.cancellations.table', target: 'cancellations' },
      { text: 'tours.cancellations.history', target: 'cancellations-actions', whenMissing: 'center' },
      exportCsv('cancellations'),
    ],
  },
  '/reports/cash-boxes': {
    key: 'screen-cash-boxes',
    steps: [
      { text: 'tours.cashBoxes.welcome' },
      { text: 'tours.cashBoxes.print', target: 'cash-boxes-print' },
      { text: 'tours.cashBoxes.stats', target: 'cash-boxes-stats', whenMissing: 'center' },
      { text: 'tours.cashBoxes.table', target: 'cash-boxes' },
      { text: 'tours.cashBoxes.detail', target: 'cash-boxes-actions', whenMissing: 'center' },
      exportCsv('cash-boxes'),
    ],
  },
  '/reports/cash-differences': {
    key: 'screen-cash-differences',
    steps: [
      { text: 'tours.cashDifferences.welcome' },
      ...filters('cash-differences'),
      { text: 'tours.cashDifferences.stats', target: 'cash-differences-stats', whenMissing: 'center' },
      { text: 'tours.cashDifferences.table', target: 'cash-differences' },
      { text: 'tours.cashDifferences.difference', target: 'cash-differences-col-cashDifference', whenMissing: 'center' },
      exportCsv('cash-differences'),
    ],
  },
  '/reports/cash-movements': {
    key: 'screen-cash-movements',
    steps: [
      { text: 'tours.cashMovements.welcome' },
      { text: 'tours.reportFilters.start', target: 'cash-movements-start' },
      { text: 'tours.reportFilters.end', target: 'cash-movements-end' },
      { text: 'tours.cashMovements.office', target: 'cash-movements-office' },
      { text: 'tours.cashMovements.details', target: 'cash-movements-details' },
      { text: 'tours.cashMovements.withoutRoute', target: 'cash-movements-without-route' },
      { text: 'tours.reportFilters.query', target: 'cash-movements-query' },
      { text: 'tours.cashMovements.table', target: 'cash-movements' },
      { text: 'tours.cashMovements.balanceAfter', target: 'cash-movements-col-balanceAfter', whenMissing: 'center' },
      exportCsv('cash-movements'),
    ],
  },
  '/reports/client-history': {
    key: 'screen-client-history',
    steps: [
      { text: 'tours.clientHistory.welcome' },
      { text: 'tours.clientHistory.office', target: 'client-history-office' },
      { text: 'tours.clientHistory.route', target: 'client-history-route' },
      { text: 'tours.clientHistory.search', target: 'client-search' },
      { text: 'tours.clientHistory.query', target: 'client-history-query' },
      { text: 'tours.clientHistory.table', target: 'client-history' },
      { text: 'tours.clientHistory.history', target: 'client-history-open', whenMissing: 'center' },
      { text: 'tours.clientHistory.creditLimit', target: 'client-history-actions', whenMissing: 'center' },
      exportCsv('client-history'),
    ],
  },
  '/reports/consolidated-day': {
    key: 'screen-consolidated-day',
    steps: [
      { text: 'tours.consolidatedDay.welcome' },
      ...filters('consolidated-day'),
      { text: 'tours.consolidatedDay.stats', target: 'consolidated-day-stats', whenMissing: 'center' },
      { text: 'tours.consolidatedDay.table', target: 'consolidated-day' },
      { text: 'tours.consolidatedDay.officeMoves', target: 'consolidated-day-col-otherIncomeTotal', whenMissing: 'center' },
      { text: 'tours.consolidatedDay.routeExpenses', target: 'consolidated-day-col-expenseTotal', whenMissing: 'center' },
      exportCsv('consolidated-day'),
    ],
  },
  '/reports/daily-summary': {
    key: 'screen-daily-summary',
    steps: [
      { text: 'tours.dailySummary.welcome' },
      ...filters('daily-summary'),
      { text: 'tours.dailySummary.table', target: 'daily-summary' },
      { text: 'tours.dailySummary.money', target: 'daily-summary-col-paymentTotal', whenMissing: 'center' },
      { text: 'tours.dailySummary.officeMoves', target: 'daily-summary-col-otherIncomeTotal', whenMissing: 'center' },
      { text: 'tours.dailySummary.cash', target: 'daily-summary-col-cash', whenMissing: 'center' },
      { text: 'tours.dailySummary.payments', target: 'daily-summary-col-payments', whenMissing: 'center' },
      exportCsv('daily-summary'),
    ],
  },
  '/reports/deleted-records': {
    key: 'screen-deleted-records',
    steps: [
      { text: 'tours.deletedRecords.welcome' },
      { text: 'tours.reportFilters.start', target: 'deleted-records-start' },
      { text: 'tours.reportFilters.end', target: 'deleted-records-end' },
      { text: 'tours.deletedRecords.office', target: 'deleted-records-office' },
      { text: 'tours.deletedRecords.route', target: 'deleted-records-route' },
      { text: 'tours.reportFilters.query', target: 'deleted-records-query' },
      { text: 'tours.deletedRecords.table', target: 'deleted-records' },
      { text: 'tours.deletedRecords.actions', target: 'deleted-records-actions', whenMissing: 'center' },
      exportCsv('deleted-records'),
    ],
  },
  '/reports/discounts': {
    key: 'screen-discounts',
    steps: [
      { text: 'tours.discounts.welcome' },
      ...filters('discounts'),
      { text: 'tours.discounts.stats', target: 'discounts-stats', whenMissing: 'center' },
      { text: 'tours.discounts.table', target: 'discounts' },
      exportCsv('discounts'),
    ],
  },
  '/reports/expenses': {
    key: 'screen-expenses-report',
    steps: [
      { text: 'tours.expensesReport.welcome' },
      { text: 'tours.reportFilters.start', target: 'expenses-report-start' },
      { text: 'tours.reportFilters.end', target: 'expenses-report-end' },
      { text: 'tours.expensesReport.office', target: 'expenses-report-office' },
      { text: 'tours.expensesReport.routes', target: 'expenses-report-routes' },
      { text: 'tours.expensesReport.category', target: 'expenses-report-category' },
      { text: 'tours.expensesReport.expenses', target: 'expenses-report-expenses' },
      { text: 'tours.reportFilters.query', target: 'expenses-report-query' },
      { text: 'tours.expensesReport.stats', target: 'expenses-report-stats', whenMissing: 'center' },
      { text: 'tours.expensesReport.table', target: 'expenses-report' },
      { text: 'tours.expensesReport.delete', target: 'expenses-report-actions', whenMissing: 'center' },
      exportCsv('expenses-report'),
    ],
  },
  '/reports/late-loans': {
    key: 'screen-late-loans',
    steps: [
      { text: 'tours.lateLoans.welcome' },
      { text: 'tours.lateLoans.start', target: 'late-loans-start' },
      { text: 'tours.reportFilters.end', target: 'late-loans-end' },
      { text: 'tours.reportFilters.office', target: 'late-loans-office' },
      { text: 'tours.reportFilters.routes', target: 'late-loans-routes' },
      { text: 'tours.reportFilters.query', target: 'late-loans-query' },
      { text: 'tours.lateLoans.table', target: 'late-loans' },
      { text: 'tours.lateLoans.daysLate', target: 'late-loans-col-daysLate', whenMissing: 'center' },
      exportCsv('late-loans'),
    ],
  },
  '/reports/loans-by-date': {
    key: 'screen-loans-by-date',
    steps: [
      { text: 'tours.loansByDate.welcome' },
      ...filters('loans-by-date'),
      { text: 'tours.loansByDate.stats', target: 'loans-by-date-stats', whenMissing: 'center' },
      { text: 'tours.loansByDate.table', target: 'loans-by-date' },
      { text: 'tours.loansByDate.actions', target: 'loans-by-date-actions', whenMissing: 'center' },
      exportCsv('loans-by-date'),
    ],
  },
  '/reports/refinancing': {
    key: 'screen-refinancing',
    steps: [
      { text: 'tours.refinancing.welcome' },
      ...filters('refinancing'),
      { text: 'tours.refinancing.table', target: 'refinancing' },
      { text: 'tours.refinancing.now', target: 'refinancing-col-balance', whenMissing: 'center' },
      exportCsv('refinancing'),
    ],
  },
  '/reports/sales-rep-risk': {
    key: 'screen-sales-rep-risk',
    steps: [
      { text: 'tours.salesRepRisk.welcome' },
      { text: 'tours.salesRepRisk.start', target: 'risk-start' },
      { text: 'tours.reportFilters.end', target: 'risk-end' },
      { text: 'tours.salesRepRisk.query', target: 'risk-search' },
      { text: 'tours.salesRepRisk.level', target: 'sales-rep-risk-col-level', whenMissing: 'center' },
      { text: 'tours.salesRepRisk.reasons', target: 'sales-rep-risk-col-reasonsText', whenMissing: 'center' },
      { text: 'tours.salesRepRisk.activity', target: 'risk-table' },
      { text: 'tours.salesRepRisk.signals', target: 'sales-rep-risk-col-signalsText', whenMissing: 'center' },
      { text: 'tours.salesRepRisk.alerts', target: 'sales-rep-risk-col-openAlerts', whenMissing: 'center' },
      exportCsv('sales-rep-risk'),
    ],
  },
};
